import {
  Component,
  ComponentRef,
  computed,
  inject,
  inputBinding,
  OnInit,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { JwtDecoderService } from '@core/services/jwt-decoder-service';
import { ProblemModal } from '@features/test-sets/problem-modal/problem-modal';
import { Button } from '@shared/components/button/button';
import { Item } from '@shared/components/item/item';
import { Select, SelectItem } from '@shared/components/select/select';
import { Toast, ToastMessage } from '@shared/components/toast/toast';
import { AllProblems, Problem } from '@shared/models/problem';
import {
  TestSet,
  TestSetFormData,
  TestSetProblem,
} from '@shared/models/test-sets';

@Component({
  selector: 'app-edit-page',
  imports: [Select, Item, Button, ProblemModal, RouterLink],
  templateUrl: './edit-page.html',
})
export class EditPage implements OnInit {
  // Injector
  private readonly apiClient = inject(ApiClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly jwtDecoder = inject(JwtDecoderService);

  // Toast functionality
  private toastRef: ComponentRef<Toast> | null = null;
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });
  private toastMessage = signal<string>('');

  // Constants
  private readonly DIFFICULTY_OPTIONS: SelectItem[] = [
    { value: 'senior', label: 'Senior' },
    { value: 'mid', label: 'Middle' },
    { value: 'junior', label: 'Junior' },
  ];

  // State
  readonly isLoading = signal<boolean>(true);
  readonly showProblemModal = signal<boolean>(false);

  // Test set
  testSet = signal<TestSet | null>(null);

  // Problem
  readonly allProblems = signal<Problem[]>([]);
  readonly selectedProblems = signal<Problem[]>([]);
  readonly toDeleteProblems = signal<TestSetProblem[]>([]);
  // Selector
  readonly selectItems = signal<SelectItem[]>(this.DIFFICULTY_OPTIONS);
  readonly selectedDifficulty = signal<SelectItem>(this.DIFFICULTY_OPTIONS[0]);

  // User
  readonly userId = computed(() => {
    const token = localStorage.getItem('accessToken');
    return token ? this.jwtDecoder.getUserId(token) : null;
  });

  async ngOnInit(): Promise<void> {
    try {
      const problems: AllProblems = await this.apiClient.getAllProblems();
      this.allProblems.set(problems.problems);

      const testSetId = this.getTestSetIdFromParams();

      if (!testSetId) {
        this.router.navigate(['/test-sets']);
        return;
      }
      await this.initializeEditMode(testSetId);
    } catch (error) {
      console.error('Error loading problems:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  private getTestSetIdFromParams(): string {
    let testSetId = '';
    this.route.paramMap.subscribe(params => {
      testSetId = params.get('testSetId') || '';
    });
    return testSetId;
  }

  private async initializeEditMode(testSetId: string): Promise<void> {
    // Load the test set data for editing
    const testSet = await this.apiClient.getTestSetDataByTestSetId(testSetId);

    if (testSet.inUsed || !testSet) {
      this.router.navigate(['/test-sets']);
      return;
    }

    this.testSet.set(testSet);
    this.setDifficultyFromTestSet(testSet);
    this.setProblemsFromTestSet(testSet);
  }

  handleDeleteProblem(problemId: string): void {
    // find that problem
    const problemToRemove = this.selectedProblems().find(
      p => p.problemId === problemId
    );

    if (!problemToRemove) return;

    // Remove from selected
    this.selectedProblems.update(current =>
      current.filter(item => item.problemId !== problemId)
    );

    // Add back to all
    this.allProblems.update(current => [...current, problemToRemove]);

    // Also add that to toDelete if it's in the original test set problems
    if (this.testSet() && this.testSet()?.problems) {
      const originalProblems = this.testSet()?.problems || [];
      this.toDeleteProblems.update(current => {
        const isInOriginal = originalProblems.some(
          p => p.problemId === problemId
        );
        return isInOriginal ? [...current, { problemId }] : current;
      });
    }
  }

  handleAddedProblems(addedProblems: Problem[]): void {
    // Add to Selected Problems
    this.selectedProblems.update(current => [...current, ...addedProblems]);

    // Remove from all
    this.allProblems.update(current =>
      current.filter(
        p => !addedProblems.some(newP => newP.problemId === p.problemId)
      )
    );

    // Also remove from toDelete
    this.toDeleteProblems.update(current =>
      current.filter(
        item => !addedProblems.some(newP => newP.problemId === item.problemId)
      )
    );
  }

  async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = this.extractFormData(form);

    try {
      await this.deleteRemovedProblems();

      const body = {
        testSetId: this.testSet()?.testSetId,
        ...formData,
      };

      await this.apiClient.editTestSets(body);

      this.router.navigate(['/test-sets/']);
    } catch (error) {
      console.error(`Error creating test set:`, error);
      this.showToast('error', 'Failed to update test set. Please try again.');
    }
  }

  private setDifficultyFromTestSet(testSet: TestSet): void {
    const matchedDifficulty = this.DIFFICULTY_OPTIONS.find(
      option => option.value === testSet.difficulty
    );

    if (matchedDifficulty) {
      this.selectedDifficulty.set(matchedDifficulty);
    }
  }

  private setProblemsFromTestSet(testSet: TestSet): void {
    const currentProblems = testSet.problems ?? [];

    const matchedProblems = this.allProblems().filter(problem =>
      currentProblems.some(
        testSetProblem => testSetProblem.problemId === problem.problemId
      )
    );

    this.selectedProblems.set(matchedProblems);

    // clear all problem follow selected problems
    this.allProblems.update(current =>
      current.filter(
        problem =>
          !currentProblems.some(
            testSetProblem => testSetProblem.problemId === problem.problemId
          )
      )
    );
  }

  private extractFormData(form: HTMLFormElement): TestSetFormData {
    const formData = new FormData(form);

    return {
      title: formData.get('testSetName') as string,
      description: formData.get('testSetDescription') as string,
      difficulty: this.selectedDifficulty().value,
      problems: this.selectedProblems().map(p => ({ problemId: p.problemId })),
    };
  }

  private async deleteRemovedProblems(): Promise<void> {
    const deletionPromises = this.toDeleteProblems().map(async item => {
      const deleteBody = {
        testSetId: this.testSet()?.testSetId,
        problemId: item.problemId,
      };

      try {
        await this.apiClient.deleteTestSetProblem(deleteBody);
      } catch (error) {
        console.error('Error deleting TestSetProblem:', error);
        throw error;
      }
    });

    await Promise.all(deletionPromises);
  }

  // Modal handlers
  openProblemModal(): void {
    this.showProblemModal.set(true);
  }

  onProblemModalClosed(): void {
    this.showProblemModal.set(false);
  }

  onProblemsAdded(addedProblems: Problem[]): void {
    this.handleAddedProblems(addedProblems);
    this.showProblemModal.set(false);
  }
}
